import {
  ColumnTable,
  Icon,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Badge } from 'reactstrap';

import IconButton from '../../../../components/buttons/IconButton';
import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  closeAcademicOffer,
  deleteAcademicOffer,
  updateAcademicOffersQuotas,
} from '../api';
import {
  NonStructuredAcademicOffer,
  NonStructuredAcademicOfferStatus,
  NonStructuredType,
} from '../types';
import { QuotaCell } from '../parts/QuotaCell';

/**
 * Obtiene el primer AcademicOfferStudyPlanVersionOffer de la oferta
 */
const getFirstAospvo = (offer: NonStructuredAcademicOffer) => {
  return offer.academicOfferStudyPlanVersionOffers?.[0];
};

/**
 * Obtiene el tipo de programa (COURSE/PROGRAM) desde NonStructuredStudyPlan
 */
const getProgramType = (
  offer: NonStructuredAcademicOffer,
): NonStructuredType | null => {
  const aospvo = getFirstAospvo(offer);
  return (
    aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.studyPlanCe
      ?.type ?? null
  );
};

/**
 * Obtiene el texto del tipo de programa
 */
const getProgramTypeText = (
  programType: NonStructuredType | null,
  t: (key: string) => string,
): string => {
  if (!programType) {
    return '-';
  }
  const texts = {
    [NonStructuredType.COURSE]: t('table.programType.course'),
    [NonStructuredType.PROGRAM_COURSE]: t('table.programType.program'),
  };
  return texts[programType] || '-';
};

/**
 * Obtiene el tipo de proceso desde admissions -> subprocess -> process -> tuitionProcessType
 */
const getProcessTypeName = (
  offer: NonStructuredAcademicOffer,
): string | null => {
  const aospvo = getFirstAospvo(offer);
  const admissions = aospvo?.admissions;
  if (!admissions || admissions.length === 0) {
    return null;
  }
  return admissions[0]?.subprocess?.process?.tuitionProcessType?.name ?? null;
};

const getBadgeColor = (status: NonStructuredAcademicOfferStatus) =>
  ({
    [NonStructuredAcademicOfferStatus.Borrador]: '#ffe6a1',
    [NonStructuredAcademicOfferStatus.Activa]: '#CEF0DA',
    [NonStructuredAcademicOfferStatus.Cerrada]: '#F2C0BD',
  }[status] ?? 'secondary');

function StatusBadgeDropdown({
  offer,
  t,
  onClose,
}: {
  offer: NonStructuredAcademicOffer;
  t: (key: string) => string;
  onClose: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const labels = {
    [NonStructuredAcademicOfferStatus.Borrador]: t('table.status.draft'),
    [NonStructuredAcademicOfferStatus.Activa]: t('table.status.active'),
    [NonStructuredAcademicOfferStatus.Cerrada]: t('table.status.closed'),
  };

  const canClose = offer.status === NonStructuredAcademicOfferStatus.Activa;

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <Badge
        className="status-badge fs-16 fw-400 py-2 px-4 text-dark"
        style={{
          backgroundColor: getBadgeColor(offer.status),
          width: '110px',
          cursor: canClose ? 'pointer' : 'default',
        }}
        pill
        onClick={() => canClose && setIsOpen(!isOpen)}
      >
        {labels[offer.status] || offer.status}
      </Badge>
      {isOpen && canClose && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              marginTop: '5px',
              minWidth: '110px',
              backgroundColor: '#fff',
              borderRadius: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                backgroundColor: '#F2C0BD',
                textAlign: 'center',
              }}
              onClick={() => {
                setIsOpen(false);
                onClose();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = 'brightness(0.95)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = 'none';
              }}
            >
              {labels[NonStructuredAcademicOfferStatus.Cerrada]}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface QuotaChange {
  id: number;
  quota: number;
}

export const useColumns = (options?: {
  onDelete?: () => void;
  onSave?: () => void;
}) => {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const history = useHistory();
  const [pendingChanges, setPendingChanges] = useState<QuotaChange[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleQuotaChange = useCallback((id: number, quota: number) => {
    setPendingChanges((prev) =>
      prev.some((c) => c.id === id)
        ? prev.map((c) => (c.id === id ? { id, quota } : c))
        : [...prev, { id, quota }],
    );
  }, []);

  const handleQuotaCancel = useCallback((id: number) => {
    setPendingChanges((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true);
    const { error } = await updateAcademicOffersQuotas(pendingChanges);
    setIsSaving(false);
    if (error) {
      const errorMessage =
        (error as any)?.data?.message === 'QUOTA_CANNOT_BE_LESS_THAN_ENROLLED'
          ? t('messages.updateQuotaCannotBeLessThanEnrolled')
          : t('messages.updateError');
      addToast({
        color: 'danger',
        icon: 'error',
        text: errorMessage,
      });
      return;
    }
    setPendingChanges([]);
    addToast({
      color: 'success',
      icon: 'success',
      text: t('messages.updateSuccess'),
    });
    options?.onSave?.();
  }, [pendingChanges, t, options]);

  const handleDelete = useCallback(
    (row: NonStructuredAcademicOffer) => {
      showDialogConfirm({
        icon: {
          color: 'warning',
          name: 'warning',
        },
        title: t('messages.deleteConfirm.title'),
        subtitle: t('messages.deleteConfirm.subtitle'),
        btnClose: {
          text: t('form.cancel'),
        },
        btnConfirm: {
          text: t('messages.deleteConfirm.confirm'),
          onConfirm: async () => {
            const { error } = await deleteAcademicOffer(row.id);
            if (error) {
              addToast({
                color: 'danger',
                icon: 'error',
                text: t('messages.deleteError'),
              });
              return;
            }
            addToast({
              color: 'success',
              icon: 'success',
              text: t('messages.deleteSuccess'),
            });
            options?.onDelete?.();
          },
        },
      });
    },
    [t, options],
  );

  const handleClose = useCallback(
    (row: NonStructuredAcademicOffer) => {
      showDialogConfirm({
        icon: {
          color: 'warning',
          name: 'warning',
        },
        title: t('messages.closeConfirm.title'),
        subtitle: t('messages.closeConfirm.subtitle'),
        btnClose: {
          text: t('form.cancel'),
        },
        btnConfirm: {
          text: t('messages.closeConfirm.confirm'),
          onConfirm: async () => {
            const { error } = await closeAcademicOffer(row.id);
            if (error) {
              addToast({
                color: 'danger',
                icon: 'error',
                text: t('messages.closeError'),
              });
              return;
            }
            addToast({
              color: 'success',
              icon: 'success',
              text: t('messages.closeSuccess'),
            });
            options?.onDelete?.();
          },
        },
      });
    },
    [t, options],
  );

  const columns = useMemo<ColumnTable<NonStructuredAcademicOffer>[]>(() => {
    return [
      {
        columnName: 'processType',
        headerText: t('table.headers.processType'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getProcessTypeName(row) ?? '-',
      },
      {
        columnName: 'academicLevel',
        headerText: t('table.headers.academicLevel', 'Nivel Académico'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return (
            aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan
              ?.academicLevel?.name ?? '-'
          );
        },
      },
      {
        columnName: 'programType',
        headerText: t('table.headers.programType'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getProgramTypeText(getProgramType(row), t),
      },
      {
        columnName: 'name',
        headerText: t('table.headers.name'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return (
            aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.name ??
            '-'
          );
        },
      },
      {
        columnName: 'modality',
        headerText: t('table.headers.modality'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return (
            aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.modality
              ?.name ?? '-'
          );
        },
      },
      {
        columnName: 'school',
        headerText: t('table.headers.school'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return (
            aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.school
              ?.name ?? '-'
          );
        },
      },
      {
        columnName: 'campus',
        headerText: t('table.headers.campus'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.studyPlanVersionOffer?.campus?.name ?? '-';
        },
      },
      {
        columnName: 'startDate',
        headerText: t('table.headers.startDate'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.startDate ?? '-';
        },
      },
      {
        columnName: 'endDate',
        headerText: t('table.headers.endDate'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.endDate ?? '-';
        },
      },
      {
        columnName: 'quota',
        headerText: t('table.headers.quota'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          if (!aospvo) {
            return '-';
          }
          const pending = pendingChanges.find((c) => c.id === aospvo.id);
          return (
            <QuotaCell
              aospvoId={aospvo.id}
              initialQuota={aospvo.quota}
              pendingValue={pending?.quota}
              quotaHistories={aospvo.offerQuotaHistories}
              onChange={handleQuotaChange}
              onCancel={handleQuotaCancel}
              disabled={isSaving}
            />
          );
        },
      },
      {
        columnName: 'enrolled',
        headerText: t('table.headers.enrolled'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getFirstAospvo(row)?.enrolledCount ?? 0,
      },
      {
        columnName: 'status',
        headerText: t('table.headers.status'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <StatusBadgeDropdown
            offer={row}
            t={t}
            onClose={() => handleClose(row)}
          />
        ),
      },
      {
        columnName: 'actions',
        headerText: t('table.headers.actions'),
        thClassName: 'text-center',
        tdClassName: 'd-flex gap-2 justify-content-center align-items-center',
        cellFormat: ({ row }) => {
          const canDelete =
            row.status === NonStructuredAcademicOfferStatus.Borrador;
          const isClosed =
            row.status === NonStructuredAcademicOfferStatus.Cerrada;
          return (
            <>
              <IconButton
                aria-label={
                  isClosed ? t('table.actions.view') : t('table.actions.edit')
                }
                title={
                  isClosed ? t('table.actions.view') : t('table.actions.edit')
                }
                onClick={() =>
                  history.push(
                    `${PathsLayouts.nonStructured}/academic-offers/${row.id}/wizard`,
                  )
                }
              >
                <Icon
                  name={isClosed ? 'eye' : 'edit'}
                  size={20}
                  color="primary"
                />
              </IconButton>
              <IconButton
                aria-label={t('table.actions.delete')}
                title={t('table.actions.delete')}
                onClick={() => handleDelete(row)}
                disabled={!canDelete}
              >
                <Icon
                  name="trash"
                  size={20}
                  color={canDelete ? 'primary' : 'disabled'}
                />
              </IconButton>
            </>
          );
        },
      },
    ];
  }, [
    t,
    history,
    handleDelete,
    handleClose,
    handleQuotaChange,
    handleQuotaCancel,
    isSaving,
    pendingChanges,
  ]);

  return { columns, pendingChanges, isSaving, handleSaveChanges };
};
